import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { ModalLoader } from "@/shared/widgets/Modal/ModalLoader";
import { last, snakeCase, toUpper } from "lodash";

export interface ModalMeta {
  [name: string]: any;
}

export type Modal = {
  id: string;
  open: boolean;
  meta?: ModalMeta;
};

interface ModalState {
  modalStack: Modal[];
  modalStackProps: ModalMeta;
}

type ModalAction =
  | {
      type: "OPEN_MODAL";
      payload: {
        modalFileName: string;
        meta: ModalMeta;
        modalStackProps?: ModalMeta;
      };
    }
  | {
      type: "CLOSE_MODAL";
      payload: { modalFileName: string; modalStackProps?: ModalMeta };
    }
  | {
      type: "UPDATE_MODAL";
      payload: {
        modalFileName: string;
        meta: ModalMeta;
        modalStackProps?: ModalMeta;
      };
    }
  | { type: "CLEAR_MODALS" };

const initialState: ModalState = {
  modalStack: [],
  modalStackProps: {},
};

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case "OPEN_MODAL": {
      const { modalFileName, meta, modalStackProps } = action.payload;
      const modalExists = state.modalStack.some(
        (modal) => modal.id === modalFileName
      );

      const newStack = modalExists
        ? state.modalStack
        : [...state.modalStack, { id: modalFileName, open: true, meta }];

      return {
        modalStack: newStack,
        modalStackProps: modalStackProps
          ? { ...state.modalStackProps, ...modalStackProps }
          : state.modalStackProps,
      };
    }
    case "CLOSE_MODAL": {
      const { modalStackProps } = action.payload;
      const newStack = state.modalStack.slice(0, -1);

      return {
        modalStack: newStack,
        modalStackProps:
          newStack.length === 0
            ? {}
            : { ...state.modalStackProps, ...(modalStackProps || {}) },
      };
    }
    case "UPDATE_MODAL": {
      const { modalFileName, meta, modalStackProps } = action.payload;
      const modalIndex = state.modalStack.findIndex(
        (modal) => modal.id === modalFileName
      );

      if (modalIndex === -1) {
        return state; // Modal not found, no change
      }

      const updatedStack = [...state.modalStack];
      updatedStack[modalIndex] = {
        ...updatedStack[modalIndex],
        meta: { ...updatedStack[modalIndex].meta, ...meta },
      };

      return {
        modalStack: updatedStack,
        modalStackProps: modalStackProps
          ? { ...state.modalStackProps, ...modalStackProps }
          : state.modalStackProps,
      };
    }
    case "CLEAR_MODALS":
      if (
        state.modalStack.length === 0 &&
        Object.keys(state.modalStackProps).length === 0
      ) {
        return state; // No change, so no re-render
      }
      return initialState;
    default:
      return state;
  }
};

interface ModalContextType {
  state: ModalState;
  openModal: (
    modalFileName: string,
    meta: ModalMeta,
    modalStackProps?: ModalMeta
  ) => void;
  closeModal: (modalFileName: string, modalStackProps?: ModalMeta) => void;
  updateModal: (
    modalFileName: string,
    meta: ModalMeta,
    modalStackProps?: ModalMeta
  ) => void;
  clearModals: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

interface IModalProviderProps {
  children: ReactNode;
}

// Modals from the url params are currently opened at PrivateRoute.tsx.
// This is because the useLocation causes an infinite re-render of the modal
// context when used there.
function ModalProvider({ children }: IModalProviderProps) {
  const [state, dispatch] = useReducer(modalReducer, initialState);
  const visibleModal = useMemo(
    () => last(state.modalStack),
    [state.modalStack]
  );

  const openModal = useCallback(
    (modalFileName: string, meta: ModalMeta, modalStackProps?: ModalMeta) => {
      dispatch({
        type: "OPEN_MODAL",
        payload: { modalFileName, meta, modalStackProps },
      });
    },
    [dispatch]
  );

  const closeModal = useCallback(
    (modalFileName: string, modalStackProps?: ModalMeta) => {
      dispatch({
        type: "CLOSE_MODAL",
        payload: { modalFileName, modalStackProps },
      });
    },
    [dispatch]
  );

  const updateModal = useCallback(
    (modalFileName: string, meta: ModalMeta, modalStackProps?: ModalMeta) => {
      dispatch({
        type: "UPDATE_MODAL",
        payload: { modalFileName, meta, modalStackProps },
      });
    },
    [dispatch]
  );

  const clearModals = useCallback(() => {
    dispatch({ type: "CLEAR_MODALS" });
  }, [dispatch]);

  return (
    <ModalContext.Provider
      value={{
        state,
        openModal,
        closeModal,
        updateModal,
        clearModals,
      }}
    >
      {children}
      {visibleModal && (
        <ModalLoader
          filename={visibleModal.id}
          onClose={(stackProps) => closeModal(visibleModal.id, stackProps)}
          onUpdate={(meta, stackProps) =>
            updateModal(visibleModal.id, meta, stackProps)
          }
          {...(visibleModal.meta || {})}
        />
      )}
    </ModalContext.Provider>
  );
}

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    return {
      state: initialState,
      openModal: (
        modalFileName: string,
        meta: ModalMeta,
        modalStackProps?: ModalMeta
      ) => {
        console.log("Application bundle has been updated. Reloading...");
        // eslint-disable-next-line react-compiler/react-compiler
        (window as any).location.search = `?modal=${toUpper(
          snakeCase(modalFileName)
        )}&${new URLSearchParams(meta).toString()}`;
      },
      closeModal: () => {},
      updateModal: () => {},
      clearModals: () => {},
    };
  }
  return context;
};

export { ModalProvider };
